
import { Users, ChevronDown } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from "@/components/ui/collapsible";
import { AvatarGroup } from "@/components/ui/avatar-group";
import { Badge } from "@/components/ui/badge";

interface Guest {
  user_id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
}

interface GuestListProps {
  guests: Guest[];
  selectedGuestsCount: number;
  onRemoveGuest?: (userId: string) => void;
  isHost: boolean;
  hostUserId: string;
}

export const GuestList = ({ 
  guests, 
  selectedGuestsCount, 
  onRemoveGuest, 
  isHost,
  hostUserId 
}: GuestListProps) => {
  const [removedGuests, setRemovedGuests] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(true);
  const { toast } = useToast();

  const getInitials = (firstName: string = '', lastName: string = '') => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'G';
  };

  const handleRemoveGuest = async (userId: string) => {
    if (onRemoveGuest) {
      try {
        setRemovedGuests(prev => [...prev, userId]);
        await onRemoveGuest(userId);
        toast({
          title: "Success",
          description: "Guest removed successfully",
        });
      } catch (error: any) {
        setRemovedGuests(prev => prev.filter(id => id !== userId));
        toast({
          title: "Error",
          description: error.message || "Failed to remove guest",
          variant: "destructive",
        });
      }
    }
  };

  const filteredGuests = guests.filter(guest => !removedGuests.includes(guest.user_id));

  // Transform Guest[] to User[] for AvatarGroup
  const guestsForAvatarGroup = filteredGuests.map(guest => ({
    full_name: `${guest.first_name} ${guest.last_name}`.trim(),
    rsvp_status: 'invited' as 'invited' | 'accepted' | 'declined' | 'maybe'
  }));

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="mb-8"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Users className="w-5 h-5 text-gray-500" />
          <div className="flex items-center gap-2">
            <h3 className="font-medium">
              Invited Guests ({filteredGuests.length})
            </h3>
            <AvatarGroup 
              users={guestsForAvatarGroup} 
              maxVisible={4} 
              size="sm"
              className="[&_[role=img]]:bg-white [&_[role=img]]:text-black [&_[role=img]]:border-2 [&_[role=img]]:border-black"
            />
          </div>
        </div>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm">
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
      </div>
      
      <CollapsibleContent className="space-y-3">
        {filteredGuests?.map((guest) => (
          <div
            key={guest.user_id}
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <div className="flex items-center gap-3 flex-1">
              <Avatar className="h-10 w-10 bg-white text-black border-2 border-black">
                <AvatarFallback className="font-medium">
                  {getInitials(guest.first_name, guest.last_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-1 items-center justify-between">
                <div>
                  <p className="font-medium">
                    {guest.first_name || guest.last_name 
                      ? `${guest.first_name || ''} ${guest.last_name || ''}`
                      : 'Guest'}
                  </p>
                  {guest.phone_number && (
                    <p className="text-sm text-gray-500">{guest.phone_number}</p>
                  )}
                </div>
                {guest.user_id === hostUserId && (
                  <Badge variant="secondary" className="text-xs ml-2">
                    Organizer
                  </Badge>
                )}
              </div>
            </div>
            {isHost && onRemoveGuest && guest.user_id !== hostUserId && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveGuest(guest.user_id)}
                className="text-gray-500 hover:text-red-500 ml-2"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};
